/**
 * Leadership Grid Block - Interactive Modal & Bio Navigation.
 */

export const initLeadershipGrid = () => {
	const blocks = document.querySelectorAll("[data-leadership-grid]");
	if (!blocks.length) return;

	blocks.forEach((block) => {
		if (block.dataset.lgInit) return;
		block.dataset.lgInit = "true";

		const modal = block.querySelector(".lg-modal");
		if (!modal) return;

		const dialogs = modal.querySelectorAll(".lg-modal-dialog");
		if (!dialogs.length) return;

		let activeIndex = 0;
		let lastFocusedElement = null;

		const showSlide = (index) => {
			const total = dialogs.length;
			if (index < 0) index = total - 1;
			if (index >= total) index = 0;
			activeIndex = index;

			dialogs.forEach((dialog) => {
				const dialogIdx = parseInt(dialog.dataset.modalIndex, 10);
				if (dialogIdx === activeIndex) {
					dialog.style.display = "flex";
					dialog.classList.add("is-active");
				} else {
					dialog.style.display = "none";
					dialog.classList.remove("is-active");
				}
			});
		};

		const openModal = (index, triggerElement) => {
			lastFocusedElement = triggerElement || document.activeElement;
			showSlide(index);

			modal.style.display = "flex";
			// Force reflow for CSS transition
			void modal.offsetWidth;
			requestAnimationFrame(() => {
				modal.classList.add("is-active");
			});
			modal.setAttribute("aria-hidden", "false");
			document.body.classList.add("lg-modal-open");

			const activeDialog = modal.querySelector(
				`.lg-modal-dialog[data-modal-index="${activeIndex}"]`,
			);
			const closeBtn = activeDialog
				? activeDialog.querySelector(".lg-modal-close")
				: modal.querySelector(".lg-modal-close");
			if (closeBtn) {
				closeBtn.focus();
			}
		};

		const closeModal = () => {
			modal.classList.remove("is-active");
			modal.setAttribute("aria-hidden", "true");
			document.body.classList.remove("lg-modal-open");

			setTimeout(() => {
				if (!modal.classList.contains("is-active")) {
					modal.style.display = "none";
				}
				if (
					lastFocusedElement &&
					typeof lastFocusedElement.focus === "function"
				) {
					lastFocusedElement.focus();
				}
			}, 300);
		};

		// Card click / keydown to open modal (only for cards with managed bios)
		block.addEventListener("click", (e) => {
			const card = e.target.closest(
				".lg-card--clickable[data-modal-target]",
			);
			if (!card || !block.contains(card)) return;

			// Prevent opening modal if clicking direct link (e.g. LinkedIn)
			if (e.target.closest(".lg-card-linkedin")) return;

			e.preventDefault();
			const targetIndex = parseInt(card.dataset.modalTarget, 10);
			if (!isNaN(targetIndex) && targetIndex >= 0) {
				openModal(targetIndex, card);
			}
		});

		block.addEventListener("keydown", (e) => {
			if (e.key !== "Enter" && e.key !== " ") return;
			const card = e.target.closest(
				".lg-card--clickable[data-modal-target]",
			);
			if (!card || !block.contains(card)) return;
			if (e.target.closest(".lg-card-linkedin")) return;

			e.preventDefault();
			const targetIndex = parseInt(card.dataset.modalTarget, 10);
			if (!isNaN(targetIndex) && targetIndex >= 0) {
				openModal(targetIndex, card);
			}
		});

		// Modal click handling (Prev, Next, Close, Backdrop)
		modal.addEventListener("click", (e) => {
			const prevBtn = e.target.closest(".lg-modal-btn--prev");
			if (prevBtn) {
				e.preventDefault();
				const target = parseInt(prevBtn.dataset.targetIndex, 10);
				showSlide(isNaN(target) ? activeIndex - 1 : target);
				return;
			}

			const nextBtn = e.target.closest(".lg-modal-btn--next");
			if (nextBtn) {
				e.preventDefault();
				const target = parseInt(nextBtn.dataset.targetIndex, 10);
				showSlide(isNaN(target) ? activeIndex + 1 : target);
				return;
			}

			const closeBtn = e.target.closest("[data-lg-close]");
			if (closeBtn) {
				e.preventDefault();
				closeModal();
			}
		});

		// Keyboard navigation inside modal
		modal.addEventListener("keydown", (e) => {
			if (!modal.classList.contains("is-active")) return;

			if (e.key === "Escape") {
				e.preventDefault();
				closeModal();
				return;
			}

			if (e.key === "ArrowLeft") {
				e.preventDefault();
				showSlide(activeIndex - 1);
				return;
			}

			if (e.key === "ArrowRight") {
				e.preventDefault();
				showSlide(activeIndex + 1);
				return;
			}

			// Focus Trap
			if (e.key === "Tab") {
				const activeDialog = modal.querySelector(
					`.lg-modal-dialog[data-modal-index="${activeIndex}"]`,
				);
				if (!activeDialog) return;

				const focusables = activeDialog.querySelectorAll(
					'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
				);
				if (!focusables.length) return;

				const first = focusables[0];
				const last = focusables[focusables.length - 1];

				if (e.shiftKey) {
					if (document.activeElement === first) {
						e.preventDefault();
						last.focus();
					}
				} else {
					if (document.activeElement === last) {
						e.preventDefault();
						first.focus();
					}
				}
			}
		});
	});
};

// Self-initialise on DOMContentLoaded or immediately if DOM is already ready.
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initLeadershipGrid);
} else {
	initLeadershipGrid();
}

// ACF Gutenberg Preview hook.
if (typeof window !== "undefined" && window.acf) {
	window.acf.addAction(
		"render_block_preview/type=leadership-grid",
		initLeadershipGrid,
	);
}

export default initLeadershipGrid;
